import type { PartnerAccount } from "@stockline/shared/types/crm/partner-account"
import { offlineDb } from "../../../lib/offline/db"
import { PARTNER_ACCOUNT_TABLE } from "../../../lib/offline/resources/crm/partner-account/db"
import { useSharedLiveQuery } from "../../../lib/offline/use-shared-live-query"

const EMPTY: PartnerAccount[] = []

/**
 * Reactive list of partner accounts (encours). Each partner has at most one
 * account row (UNIQUE constraint on `partner_id`), so this is typically the
 * same cardinality as the partners table. Shared via `useSharedLiveQuery`
 * to amortise the scan across every recap PartnerAccountBadge.
 *
 * Tenant isolation: Dexie is single-tenant by construction — each
 * AISyncProvider instance only pulls + receives events for the current
 * company. Adding a `companyId` filter here would be defensive but is
 * consistent with all other CRM hooks (see `usePartners`, etc.) which
 * also rely on the same invariant.
 */
export function usePartnerAccounts(): PartnerAccount[] {
  const rows = useSharedLiveQuery<PartnerAccount[]>(
    "partner-accounts:all",
    async () => {
      const table = offlineDb.table<PartnerAccount>(PARTNER_ACCOUNT_TABLE)
      const all = await table.toArray()
      return all.filter((r) => !r.deletedAt)
    }
  )
  return rows ?? EMPTY
}

/**
 * Look up the partner account for a single partner. Returns `undefined`
 * while loading, `null` if the partner has no account row yet.
 *
 * Hot path: rendered inside the recap PartnerAccountBadge for every
 * visible partner. Dexie's `[companyId+partnerId]` compound index lets the
 * underlying `where().equals()` resolve in O(log n) per partner.
 */
export function usePartnerAccount(
  partnerId: string | null
): PartnerAccount | null | undefined {
  const cacheKey = partnerId
    ? `partner-account:partnerId=${partnerId}`
    : "partner-account:null"
  const row = useSharedLiveQuery<PartnerAccount | null>(cacheKey, async () => {
    if (!partnerId) return null
    const table = offlineDb.table<PartnerAccount>(PARTNER_ACCOUNT_TABLE)
    const account = await table.where("partnerId").equals(partnerId).first()
    if (!account || account.deletedAt) return null
    return account
  })
  return row
}
